import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Download, Search as SearchIcon, Edit, X, Save, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { recalcAll, getDefaultHsn, getEffectiveGstRate } from '@/lib/gstHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [editingBill, setEditingBill] = useState(null);
  const [originalBillNo, setOriginalBillNo] = useState(null);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [userRole, setUserRole] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState({
    fy: '2026-27',
    magazine: '',
    bill_no: '',
    entity_id: '',
    company_name: '',
    type: ''
  });

  // Get current user role
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    
    try {
      const searchParams = {
        ...searchCriteria,
        document_type: 'Bill'
      };
      const response = await axios.post(`${API}/bills/search`, searchParams);
      
      // Sort bills by bill_no (largest to smallest) - extract numeric part
      const sortedBills = response.data.sort((a, b) => {
        const extractNumber = (billNo) => {
          const match = String(billNo).match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        return extractNumber(b.bill_no) - extractNumber(a.bill_no); // b - a for descending
      });
      
      setBills(sortedBills);
      
      if (sortedBills.length === 0) {
        toast.info('No bills found matching the criteria');
      } else {
        toast.success(`Found ${sortedBills.length} bill(s)`);
      }
    } catch (error) {
      console.error('Error searching bills:', error);
      toast.error('Failed to search bills');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedBills = () => {
    if (!sortColumn) return bills;

    return [...bills].sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Handle date columns
      if (sortColumn === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle numeric columns
      else if (sortColumn === 'gross_amount' || sortColumn === 'total_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      // Handle string columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleReset = () => {
    setSearchCriteria({
      magazine: '',
      bill_no: '',
      entity_id: '',
      company_name: ''
    });
    setBills([]);
    setSortColumn('');
    setSortDirection('asc');
  };

  // Auto-compute GST + total via shared gstHelper.recalcAll (supports reverse math).
  const recalcGSTAndTotal = (state) => {
    const n = recalcAll({
      ...state,
      gross_amount: parseFloat(state.gross_amount) || 0,
      discount_amount: parseFloat(state.discount_amount) || 0,
      total_amount: parseFloat(state.total_amount) || 0,
    });
    return {
      gross_amount: state.last_edited_field === 'total' ? (n.gross_amount ?? state.gross_amount) : state.gross_amount,
      sgst_amount: n.sgst_amount,
      cgst_amount: n.cgst_amount,
      igst_amount: n.igst_amount,
      total_amount: n.total_amount,
    };
  };

  const handleEdit = (bill) => {
    try {
      console.log('Opening edit modal for bill:', bill);
      console.log('Bill terms_conditions:', bill.terms_conditions);
      console.log('Bill item_description:', bill.item_description);
      
      // Default detailed terms if stored terms are short or empty
      const defaultDetailedTerms = '1. Artwork approval required before printing for multiple issues.\n2. Post-order confirmation, PO with full terms should be provided.\n3. Dimensions: Full Page Size of 7.25" x 10.5" (A4)\n4. Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)\n5. 1 print copy shall be sent at your registered postal address.';
      
      // Use stored terms if they have more than 100 characters (i.e., detailed terms), else use default
      const termsToShow = (bill.terms_conditions && bill.terms_conditions.length > 100) 
        ? bill.terms_conditions 
        : defaultDetailedTerms;
      
      // Generate item description if not stored - same logic as PrintBill.js
      const generateItemDescription = () => {
        const product = bill.product === 'Renewal' ? 'Subscription Renewal' : bill.product || 'Subscription';
        const tenure = bill.period_duration || bill.advertisement_tenure || '1 Month';
        const occasion = bill.occasion && bill.occasion !== 'None' ? ` in occasion ${bill.occasion}` : '';
        return `Towards your ${product} charges of ${bill.magazine} magazine for ${tenure}${occasion}\nHSN Code: 998363`;
      };
      
      // Use stored item_description if exists, else generate it
      const itemDescToShow = bill.item_description || generateItemDescription();
      
      // Ensure all fields have proper defaults to avoid undefined/null values in Select components
      setEditingBill({ 
        ...bill,
        fy: bill.fy || '2024-25',
        document_type: bill.document_type || 'Bill',
        magazine: bill.magazine || '',
        type: bill.type || '',
        product: bill.product || '',
        initial: bill.initial || 'M/s.',
        billing_state: bill.billing_state || '',
        occasion: bill.occasion || '',
        bill_date: bill.bill_date || new Date().toISOString().split('T')[0],
        po_wo_no: bill.po_wo_no || '',
        proforma_invoice_no: bill.proforma_invoice_no || '',
        entity_id: bill.entity_id || '',
        billing_name: bill.billing_name || '',
        bill_receipt_name: bill.bill_receipt_name || '',
        billing_address: bill.billing_address || '',
        district: bill.district || '',
        pincode: bill.pincode || '',
        gst_no: bill.gst_no || '',
        start_month: bill.start_month || '',
        start_year: bill.start_year || '',
        end_month: bill.end_month || '',
        end_year: bill.end_year || '',
        advertisement_position: bill.advertisement_position || '',
        advertisement_tenure: bill.advertisement_tenure || '',
        item_description: itemDescToShow,
        terms_conditions: termsToShow,
        gross_amount: bill.gross_amount || 0,
        discount_percentage: bill.discount_percentage || '',
        discount_amount: bill.discount_amount || '',
        sgst_amount: bill.sgst_amount || 0,
        cgst_amount: bill.cgst_amount || 0,
        igst_amount: bill.igst_amount || 0,
        total_amount: bill.total_amount || 0,
        gst_rate: bill.gst_rate || 'auto',
        hsn_sac: bill.hsn_sac || getDefaultHsn(bill.product || ''),
        last_edited_field: '',
      });
      setOriginalBillNo(bill.bill_no);
      console.log('Edit modal state set successfully');
    } catch (error) {
      console.error('Error in handleEdit:', error);
      toast.error('Failed to open edit modal: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingBill(null);
    setOriginalBillNo(null);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data matching BillCreate model (exclude id, created_at, updated_at, etc.)
      // Ensure bill_date is in correct format (YYYY-MM-DD)
      let billDateToSave = editingBill.bill_date || '';
      if (billDateToSave && !billDateToSave.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // If it's in DD/MM/YYYY format, convert to YYYY-MM-DD
        const parts = billDateToSave.split('/');
        if (parts.length === 3) {
          billDateToSave = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }

      const updateData = {
        fy: editingBill.fy,
        document_type: editingBill.document_type,
        magazine: editingBill.magazine,
        type: editingBill.type,
        product: editingBill.product,
        po_wo_no: editingBill.po_wo_no,
        proforma_invoice_no: editingBill.proforma_invoice_no,
        entity_id: editingBill.entity_id,
        initial: editingBill.initial,
        billing_name: editingBill.billing_name,
        bill_receipt_name: editingBill.bill_receipt_name,
        billing_address: editingBill.billing_address || '',
        billing_state: editingBill.billing_state,
        district: editingBill.district || '',
        pincode: editingBill.pincode || '',
        gst_no: editingBill.gst_no || '',
        occasion: editingBill.occasion,
        bill_no: editingBill.bill_no || '',
        bill_date: billDateToSave,
        start_month: editingBill.start_month || '',
        start_year: editingBill.start_year || '',
        end_month: editingBill.end_month || '',
        end_year: editingBill.end_year || '',
        advertisement_position: editingBill.advertisement_position || '',
        advertisement_tenure: editingBill.advertisement_tenure || '',
        item_description: editingBill.item_description || '',
        include_in_aabkari: editingBill.include_in_aabkari || false,
        gross_amount: parseFloat(editingBill.gross_amount) || 0,
        discount_percentage: parseFloat(editingBill.discount_percentage) || 0,
        discount_amount: parseFloat(editingBill.discount_amount) || 0,
        sgst_amount: parseFloat(editingBill.sgst_amount) || 0,
        cgst_amount: parseFloat(editingBill.cgst_amount) || 0,
        igst_amount: parseFloat(editingBill.igst_amount) || 0,
        total_amount: parseFloat(editingBill.total_amount) || 0,
        amount_in_words: editingBill.amount_in_words || '',
        gst_rate: editingBill.gst_rate || 'auto',
        hsn_sac: editingBill.hsn_sac || '',
        terms_conditions: editingBill.terms_conditions || ''
      };
      
      console.log('Saving bill with bill_date:', billDateToSave);
      console.log('Full update data:', updateData);
      
      await axios.put(`${API}/bills/${originalBillNo}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Bill updated successfully');
      setEditingBill(null);
      setOriginalBillNo(null);
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error updating bill:', error);
      toast.error('Failed to update bill: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (billNo) => {
    if (!window.confirm(`Are you sure you want to delete Bill No: ${billNo}?`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bills/${billNo}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Bill deleted successfully');
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error deleting bill:', error);
      toast.error('Failed to delete bill');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    document.title = 'Bill Report';
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  const exportToCSV = () => {
    if (bills.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Get sorted bills to export in the same order as displayed
      const sortedBills = getSortedBills();
      
      const headers = ['Bill No', 'Document Type', 'Magazine', 'Type', 'Billing Name', 'Billing State', 'GST No', 'Gross Amount', 'GST Amount', 'Total Amount', 'Created Date'];
      const csvContent = [
        headers.join(','),
        ...sortedBills.map(bill => {
          const totalGST = (bill.sgst_amount || 0) + (bill.cgst_amount || 0) + (bill.igst_amount || 0) || (bill.gst_amount || 0);
          return [
            bill.bill_no || '',
            bill.document_type || '',
            bill.magazine || '',
            bill.type || '',
            `"${bill.billing_name || ''}"`,
            bill.billing_state || '',
            bill.gst_no || '',
            bill.gross_amount || 0,
            totalGST.toFixed(2),
            bill.total_amount || 0,
            bill.created_at ? new Date(bill.created_at).toLocaleString('en-IN') : ''
          ].join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `Bill_Report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded! Check your Downloads folder');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() + i - 2).toString());

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }} data-testid="reports-title">
              Bill Reports
            </h1>
          </div>
          {bills.length > 0 && (
            <div className="flex space-x-2">
              <Button onClick={exportToCSV} className="btn-secondary" data-testid="export-button">
                <Download className="mr-2" size={18} />
                Export to Excel
              </Button>
              <Button onClick={handlePrint} className="btn-primary" style={{ backgroundColor: '#7c3aed' }} data-testid="print-button">
                <FileText className="mr-2" size={18} />
                Print Report
              </Button>
            </div>
          )}
        </div>

        <form onSubmit={handleSearch} className="form-section mb-8 no-print">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#7c3aed' }}>Search Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Financial Year *</Label>
              <Select value={searchCriteria.fy} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, fy: value })}>
                <SelectTrigger data-testid="fy-filter">
                  <SelectValue placeholder="Select FY" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026-27">2026-27</SelectItem>
                  <SelectItem value="2025-26">2025-26</SelectItem>
                  <SelectItem value="2024-25">2024-25</SelectItem>
                  <SelectItem value="2023-24">2023-24</SelectItem>
                  <SelectItem value="2022-23">2022-23</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={searchCriteria.magazine} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, magazine: value })}>
                <SelectTrigger data-testid="magazine-filter">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Bill No</Label>
              <Input data-testid="bill-no-input" value={searchCriteria.bill_no} onChange={(e) => setSearchCriteria({ ...searchCriteria, bill_no: e.target.value })} placeholder="Enter bill number" />
            </div>

            <div className="space-y-2">
              <Label>ID</Label>
              <Input data-testid="entity-id-input" value={searchCriteria.entity_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, entity_id: e.target.value })} placeholder="Enter ID" />
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={searchCriteria.type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, type: value })}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Advertiser">Advertiser</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-8 flex space-x-4">
            <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#7c3aed' }} data-testid="search-button">
              <SearchIcon className="mr-2" size={18} />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
              Reset
            </Button>
          </div>
        </form>

        {/* Edit Modal */}
        {editingBill && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ overflow: 'auto' }}>
            <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#7c3aed' }}>Edit Bill</h2>
                <Button variant="outline" onClick={handleCancelEdit} size="sm">
                  <X size={18} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Bill No</Label>
                  <Input value={editingBill.bill_no} onChange={(e) => setEditingBill({ ...editingBill, bill_no: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Bill Date</Label>
                  <Input 
                    type="date"
                    value={editingBill.bill_date || ''} 
                    onChange={(e) => setEditingBill({ ...editingBill, bill_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Financial Year</Label>
                  <Input value={editingBill.fy} onChange={(e) => setEditingBill({ ...editingBill, fy: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Document Type</Label>
                  <Select value={editingBill.document_type || 'Bill'} onValueChange={(value) => setEditingBill({ ...editingBill, document_type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bill">Bill</SelectItem>
                      <SelectItem value="Proforma Invoice">Proforma Invoice</SelectItem>
                      <SelectItem value="Receipt">Receipt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <Select value={editingBill.magazine || ''} onValueChange={(value) => setEditingBill({ ...editingBill, magazine: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select magazine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={editingBill.type || ''} onValueChange={(value) => setEditingBill({ ...editingBill, type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Advertiser">Advertiser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Product</Label>
                  <Select value={editingBill.product || ''} onValueChange={(value) => setEditingBill({ ...editingBill, product: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Subscription">Subscription</SelectItem>
                      <SelectItem value="Renewal">Renewal</SelectItem>
                      <SelectItem value="Advertisement">Advertisement</SelectItem>
                      <SelectItem value="Subscription/Advertisement">Subscription/Advertisement</SelectItem>
                      <SelectItem value="Event">Event</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>P.O./W.O. No.</Label>
                  <Input value={editingBill.po_wo_no || ''} onChange={(e) => setEditingBill({ ...editingBill, po_wo_no: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Proforma Invoice No.</Label>
                  <Input value={editingBill.proforma_invoice_no || ''} onChange={(e) => setEditingBill({ ...editingBill, proforma_invoice_no: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Entity ID</Label>
                  <Input value={editingBill.entity_id || ''} onChange={(e) => setEditingBill({ ...editingBill, entity_id: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Initial</Label>
                  <Select value={editingBill.initial || 'M/s.'} onValueChange={(value) => setEditingBill({ ...editingBill, initial: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select initial" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr.">Mr.</SelectItem>
                      <SelectItem value="Mrs.">Mrs.</SelectItem>
                      <SelectItem value="Ms.">Ms.</SelectItem>
                      <SelectItem value="Dr.">Dr.</SelectItem>
                      <SelectItem value="M/s.">M/s.</SelectItem>
                      <SelectItem value="The">The</SelectItem>
                      <SelectItem value="To">To</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Billing Name</Label>
                  <Input value={editingBill.billing_name} onChange={(e) => setEditingBill({ ...editingBill, billing_name: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Bill/Receipt Name</Label>
                  <Input value={editingBill.bill_receipt_name || ''} onChange={(e) => setEditingBill({ ...editingBill, bill_receipt_name: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>GST No</Label>
                  <Input value={editingBill.gst_no || ''} onChange={(e) => setEditingBill({ ...editingBill, gst_no: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Occasion</Label>
                  <Select value={editingBill.occasion || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, occasion: value === 'NONE' ? '' : value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select occasion" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">None</SelectItem>
                      <SelectItem value="Special Issue">Special Issue</SelectItem>
                      <SelectItem value="15th August Special Issue">15th August Special Issue</SelectItem>
                      <SelectItem value="26th January Special Issue">26th January Special Issue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Billing Address Section */}
                <div className="md:col-span-3">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h3 className="text-sm font-semibold mb-3 text-purple-700">Billing Address Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Address</Label>
                        <Input value={editingBill.billing_address || ''} onChange={(e) => setEditingBill({ ...editingBill, billing_address: e.target.value })} placeholder="Street address" />
                      </div>
                      <div className="space-y-2">
                        <Label>District</Label>
                        <Input value={editingBill.district || ''} onChange={(e) => setEditingBill({ ...editingBill, district: e.target.value })} placeholder="District" />
                      </div>
                      <div className="space-y-2">
                        <Label>Billing State</Label>
                        <Select value={editingBill.billing_state || ''} onValueChange={(value) => setEditingBill({ ...editingBill, billing_state: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
                            <SelectItem value="Maharashtra">Maharashtra</SelectItem>
                            <SelectItem value="Delhi">Delhi</SelectItem>
                            <SelectItem value="Karnataka">Karnataka</SelectItem>
                            <SelectItem value="Gujarat">Gujarat</SelectItem>
                            <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
                            <SelectItem value="West Bengal">West Bengal</SelectItem>
                            <SelectItem value="Rajasthan">Rajasthan</SelectItem>
                            <SelectItem value="Punjab">Punjab</SelectItem>
                            <SelectItem value="Haryana">Haryana</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Pincode</Label>
                        <Input value={editingBill.pincode || ''} onChange={(e) => setEditingBill({ ...editingBill, pincode: e.target.value })} placeholder="Pincode" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Advertisement Details Section - Only for Advertiser type */}
                {editingBill.type === 'Advertiser' && (
                  <div className="md:col-span-3">
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <h3 className="text-sm font-semibold mb-3 text-purple-700">Advertisement Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Advertisement Position</Label>
                          <Select value={editingBill.advertisement_position || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, advertisement_position: value === 'NONE' ? '' : value })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select position" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">None</SelectItem>
                              <SelectItem value="Front Cover Inside">Front Cover Inside</SelectItem>
                              <SelectItem value="Full Page Inside">Full Page Inside</SelectItem>
                              <SelectItem value="Full Page">Full Page</SelectItem>
                              <SelectItem value="Back Cover Inside">Back Cover Inside</SelectItem>
                              <SelectItem value="Back Cover">Back Cover</SelectItem>
                              <SelectItem value="Half Page">Half Page</SelectItem>
                              <SelectItem value="Bottom Strip">Bottom Strip</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Advertisement Tenure</Label>
                          <Select value={editingBill.advertisement_tenure || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, advertisement_tenure: value === 'NONE' ? '' : value })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select tenure" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">None</SelectItem>
                              <SelectItem value="1 Month">1 Month</SelectItem>
                              <SelectItem value="2 Months">2 Months</SelectItem>
                              <SelectItem value="3 Months">3 Months</SelectItem>
                              <SelectItem value="4 Months">4 Months</SelectItem>
                              <SelectItem value="5 Months">5 Months</SelectItem>
                              <SelectItem value="6 Months">6 Months</SelectItem>
                              <SelectItem value="1 Year">1 Year</SelectItem>
                              <SelectItem value="Alternate Months">Alternate Months</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Period/Duration Section - Different options based on type */}
                <div className="md:col-span-3">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h3 className="text-sm font-semibold mb-3 text-purple-700">Period/Duration</h3>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label>Period/Duration</Label>
                        <Select value={editingBill.advertisement_tenure || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, advertisement_tenure: value === 'NONE' ? '' : value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select period/duration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NONE">None</SelectItem>
                            {editingBill.type === 'Member' ? (
                              <>
                                <SelectItem value="1 Year">1 Year</SelectItem>
                                <SelectItem value="2 Years">2 Years</SelectItem>
                                <SelectItem value="3 Years">3 Years</SelectItem>
                                <SelectItem value="Lifetime">Lifetime</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="1 Month">1 Month</SelectItem>
                                <SelectItem value="2 Months">2 Months</SelectItem>
                                <SelectItem value="3 Months">3 Months</SelectItem>
                                <SelectItem value="4 Months">4 Months</SelectItem>
                                <SelectItem value="5 Months">5 Months</SelectItem>
                                <SelectItem value="6 Months">6 Months</SelectItem>
                                <SelectItem value="1 Year">1 Year</SelectItem>
                                <SelectItem value="Alternate Months">Alternate Months</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Start/End Date Section */}
                <div className="md:col-span-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="text-sm font-semibold mb-3 text-blue-700">Start & End Dates</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Start Month</Label>
                        <Select value={editingBill.start_month || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, start_month: value === 'NONE' ? '' : value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select month" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NONE">None</SelectItem>
                            <SelectItem value="January">January</SelectItem>
                            <SelectItem value="February">February</SelectItem>
                            <SelectItem value="March">March</SelectItem>
                            <SelectItem value="April">April</SelectItem>
                            <SelectItem value="May">May</SelectItem>
                            <SelectItem value="June">June</SelectItem>
                            <SelectItem value="July">July</SelectItem>
                            <SelectItem value="August">August</SelectItem>
                            <SelectItem value="September">September</SelectItem>
                            <SelectItem value="October">October</SelectItem>
                            <SelectItem value="November">November</SelectItem>
                            <SelectItem value="December">December</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Start Year</Label>
                        <Select value={editingBill.start_year || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, start_year: value === 'NONE' ? '' : value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select year" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72 overflow-y-auto">
                            <SelectItem value="NONE">None</SelectItem>
                            {Array.from({ length: 25 }, (_, i) => (2021 + i).toString()).map(y => (
                              <SelectItem key={y} value={y}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>End Month</Label>
                        <Select value={editingBill.end_month || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, end_month: value === 'NONE' ? '' : value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select month" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NONE">None</SelectItem>
                            <SelectItem value="January">January</SelectItem>
                            <SelectItem value="February">February</SelectItem>
                            <SelectItem value="March">March</SelectItem>
                            <SelectItem value="April">April</SelectItem>
                            <SelectItem value="May">May</SelectItem>
                            <SelectItem value="June">June</SelectItem>
                            <SelectItem value="July">July</SelectItem>
                            <SelectItem value="August">August</SelectItem>
                            <SelectItem value="September">September</SelectItem>
                            <SelectItem value="October">October</SelectItem>
                            <SelectItem value="November">November</SelectItem>
                            <SelectItem value="December">December</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>End Year</Label>
                        <Select value={editingBill.end_year || 'NONE'} onValueChange={(value) => setEditingBill({ ...editingBill, end_year: value === 'NONE' ? '' : value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select year" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72 overflow-y-auto">
                            <SelectItem value="NONE">None</SelectItem>
                            {Array.from({ length: 25 }, (_, i) => (2021 + i).toString()).map(y => (
                              <SelectItem key={y} value={y}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Amount & GST Section */}
                {/* Item Description */}
                <div className="space-y-2 md:col-span-3">
                  <Label>Item Description (shown in printed bill)</Label>
                  <textarea
                    value={editingBill.item_description || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, item_description: e.target.value })}
                    placeholder="e.g., Towards your Subscription charges of Aabkari Times magazine for 1 Year"
                    rows={3}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                {/* Include in Aabkari checkbox */}
                {editingBill.magazine === 'Sugar Times' && editingBill.type === 'Advertiser' && (
                  <div className="space-y-2 md:col-span-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit_include_in_aabkari"
                        checked={editingBill.include_in_aabkari || false}
                        onChange={(e) => setEditingBill({ ...editingBill, include_in_aabkari: e.target.checked })}
                        className="w-4 h-4 cursor-pointer"
                        data-testid="edit-include-in-aabkari"
                      />
                      <Label htmlFor="edit_include_in_aabkari" className="cursor-pointer font-semibold">
                        Include advertisement in Aabkari Times also (Complimentary Offer)
                      </Label>
                    </div>
                  </div>
                )}

                <div className="md:col-span-3">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="text-sm font-semibold mb-3 text-green-700">Amount & GST Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="space-y-2">
                        <Label>GST Rate</Label>
                        <Select
                          value={editingBill.gst_rate || 'auto'}
                          onValueChange={(value) => {
                            const next = { ...editingBill, gst_rate: value };
                            Object.assign(next, recalcGSTAndTotal(next));
                            setEditingBill(next);
                          }}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto (by product)</SelectItem>
                            <SelectItem value="0">0%</SelectItem>
                            <SelectItem value="5">5%</SelectItem>
                            <SelectItem value="18">18%</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">Eff: {(getEffectiveGstRate(editingBill) * 100).toFixed(0)}%</p>
                      </div>
                      <div className="space-y-2">
                        <Label>HSN/SAC</Label>
                        <Input value={editingBill.hsn_sac || ''} onChange={(e) => setEditingBill({ ...editingBill, hsn_sac: e.target.value })} placeholder="998363" />
                      </div>
                      <div className="space-y-2">
                        <Label>Gross Amount</Label>
                        <Input type="number" step="0.01" value={editingBill.gross_amount} onChange={(e) => {
                          const gross = parseFloat(e.target.value) || 0;
                          const next = { ...editingBill, gross_amount: gross, last_edited_field: 'gross' };
                          const pct = parseFloat(editingBill.discount_percentage) || 0;
                          if (pct > 0) {
                            next.discount_amount = Math.round(gross * pct / 100 * 100) / 100;
                          }
                          Object.assign(next, recalcGSTAndTotal(next));
                          setEditingBill(next);
                        }} className="font-semibold" />
                      </div>

                      <div className="space-y-2">
                        <Label>Discount %</Label>
                        <Input type="number" step="0.01" value={editingBill.discount_percentage || ''} onChange={(e) => {
                          const pct = parseFloat(e.target.value) || 0;
                          const gross = parseFloat(editingBill.gross_amount) || 0;
                          const discAmt = Math.round(gross * pct / 100 * 100) / 100;
                          const next = { ...editingBill, discount_percentage: pct, discount_amount: discAmt, last_edited_field: 'discount' };
                          Object.assign(next, recalcGSTAndTotal(next));
                          setEditingBill(next);
                        }} placeholder="e.g. 20" />
                      </div>

                      <div className="space-y-2">
                        <Label>Discount Amt</Label>
                        <Input type="number" step="0.01" value={editingBill.discount_amount || ''} onChange={(e) => {
                          const discAmt = parseFloat(e.target.value) || 0;
                          const gross = parseFloat(editingBill.gross_amount) || 0;
                          const pct = gross > 0 ? Math.round(discAmt / gross * 100 * 100) / 100 : 0;
                          const next = { ...editingBill, discount_amount: discAmt, discount_percentage: pct, last_edited_field: 'discount' };
                          Object.assign(next, recalcGSTAndTotal(next));
                          setEditingBill(next);
                        }} />
                      </div>

                      <div className="space-y-2">
                        <Label>SGST</Label>
                        <Input type="number" step="0.01" value={editingBill.sgst_amount || 0} onChange={(e) => {
                          const sgst = parseFloat(e.target.value) || 0;
                          const gross = parseFloat(editingBill.gross_amount) || 0;
                          const disc = parseFloat(editingBill.discount_amount) || 0;
                          const net = gross - disc;
                          setEditingBill({ ...editingBill, sgst_amount: sgst, total_amount: net + sgst + (parseFloat(editingBill.cgst_amount) || 0) + (parseFloat(editingBill.igst_amount) || 0) });
                        }} />
                      </div>

                      <div className="space-y-2">
                        <Label>CGST</Label>
                        <Input type="number" step="0.01" value={editingBill.cgst_amount || 0} onChange={(e) => {
                          const cgst = parseFloat(e.target.value) || 0;
                          const gross = parseFloat(editingBill.gross_amount) || 0;
                          const disc = parseFloat(editingBill.discount_amount) || 0;
                          const net = gross - disc;
                          setEditingBill({ ...editingBill, cgst_amount: cgst, total_amount: net + (parseFloat(editingBill.sgst_amount) || 0) + cgst + (parseFloat(editingBill.igst_amount) || 0) });
                        }} />
                      </div>

                      <div className="space-y-2">
                        <Label>IGST</Label>
                        <Input type="number" step="0.01" value={editingBill.igst_amount || 0} onChange={(e) => {
                          const igst = parseFloat(e.target.value) || 0;
                          const gross = parseFloat(editingBill.gross_amount) || 0;
                          const disc = parseFloat(editingBill.discount_amount) || 0;
                          const net = gross - disc;
                          setEditingBill({ ...editingBill, igst_amount: igst, total_amount: net + (parseFloat(editingBill.sgst_amount) || 0) + (parseFloat(editingBill.cgst_amount) || 0) + igst });
                        }} />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label className="font-bold text-green-700">Grand Total (Inclusive of GST)</Label>
                        <Input type="number" step="0.01" value={editingBill.total_amount || 0} onChange={(e) => {
                          const next = { ...editingBill, total_amount: parseFloat(e.target.value) || 0, last_edited_field: 'total' };
                          Object.assign(next, recalcGSTAndTotal(next));
                          setEditingBill(next);
                        }} className="font-bold text-lg bg-green-100 border-green-300" />
                        <p className="text-xs text-gray-500">Type here for GST-inclusive (Taxable = Total / (1 + Rate)). Gross will reverse-calc.</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">For Subscription/Renewal: set GST Rate to 0% or leave product as Subscription. Total = Gross − Discount when no GST.</p>
                  </div>
                </div>

                <div className="space-y-2 md:col-span-3">
                  <Label>Terms & Conditions</Label>
                  <Textarea value={editingBill.terms_conditions || ''} onChange={(e) => setEditingBill({ ...editingBill, terms_conditions: e.target.value })} rows={5} />
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-4">
                <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={loading} style={{ backgroundColor: '#7c3aed', color: 'white' }}>
                  <Save className="mr-2" size={18} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loader" />
        ) : bills.length > 0 ? (
          <div className="form-section">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#7c3aed' }}>
              Total Bills: {bills.length}
            </h2>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="reports-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('bill_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Bill No {sortColumn === 'bill_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('document_type')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Document Type {sortColumn === 'document_type' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('magazine')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Magazine {sortColumn === 'magazine' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('type')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Type {sortColumn === 'type' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('billing_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Billing Name {sortColumn === 'billing_name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('billing_state')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Billing State {sortColumn === 'billing_state' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('gross_amount')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Gross Amount {sortColumn === 'gross_amount' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th>GST</th>
                    <th onClick={() => handleSort('total_amount')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Total Amount {sortColumn === 'total_amount' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Created Date {sortColumn === 'created_at' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedBills().map((bill) => {
                    // Calculate total GST for display
                    // GST should be 0 for Subscription and Renewal products (only applicable for Advertisement)
                    const isNoGSTProduct = bill.product === 'Subscription' || bill.product === 'Renewal';
                    const totalGST = isNoGSTProduct ? 0 : ((bill.sgst_amount || 0) + (bill.cgst_amount || 0) + (bill.igst_amount || 0) || (bill.gst_amount || 0));
                    return (
                      <tr key={bill.id} data-testid={`report-row-${bill.bill_no}`}>
                        <td>{bill.bill_no}</td>
                        <td>{bill.document_type}</td>
                        <td>{bill.magazine}</td>
                        <td>{bill.type}</td>
                        <td>{bill.billing_name}</td>
                        <td>{bill.billing_state || '-'}</td>
                        <td>₹{bill.gross_amount.toFixed(2)}</td>
                        <td>₹{totalGST.toFixed(2)}</td>
                        <td>₹{isNoGSTProduct ? bill.gross_amount.toFixed(2) : bill.total_amount.toFixed(2)}</td>
                        <td>
                          <div>
                            {bill.created_at ? new Date(bill.created_at).toLocaleString('en-IN') : ''}
                            {bill.created_by && (
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                by {bill.created_by}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(bill)} data-testid={`edit-${bill.bill_no}`}>
                              <Edit size={16} className="mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                              const copyData = { ...bill };
                              delete copyData.bill_no;
                              delete copyData.id;
                              delete copyData.created_at;
                              delete copyData.updated_at;
                              navigate('/enter-bill', { state: { copyFrom: copyData } });
                            }} title="Duplicate" className="text-blue-600 border-blue-300 hover:bg-blue-50" data-testid={`copy-${bill.bill_no}`}>
                              <Copy size={16} className="mr-1" />
                              Copy
                            </Button>
                            {userRole === 'admin' && (
                              <Button size="sm" variant="outline" onClick={() => handleDelete(bill.bill_no)} data-testid={`delete-${bill.bill_no}`} className="text-red-600 hover:text-red-700">
                                <Trash2 size={16} className="mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="form-section">
            <p className="text-gray-600 text-center">Use search filters above to find bills and generate reports.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillReports;